import { Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import Note from '@/designs-dev/annotations/Note';
import { type HandoverSpec } from '@/designs-dev/handover/types';
import ActionsScreen from '@/karehero-app/actions/ActionsScreen';
import { type Item } from '@/components/wrapped/ActionCard';
import AccountBar from '@/karehero-app/access/AccountBar';
import StatusBar from '@/designs-dev/device/StatusBar';
import {
  HELD,
  NAME,
  WITH_HELD,
  chat,
  homeTab,
} from '@/karehero-app/handover/dynamic-flow-slices';
import HomeScreen from '@/karehero-app/home/HomeScreen';
import AppTab from '@/karehero-app/shell/AppTab';
import ChatOption from '@/karehero-app/tell-us-once/intake/ChatOption';
import { type ChatQuestion } from '@/karehero-app/tell-us-once/intake/content';
import Flow from '@/karehero-app/tell-us-once/intake/Flow';
import { CALCULATOR_RESULT } from '@/karehero-app/tell-us-once/benefits/BenefitsCalculator.content';
import FromUsCard from '@/karehero-app/tell-us-once/intake/FromUsCard';
import HomeConcept2 from '@/karehero-app/tell-us-once/intake/HomeConcept2';

const noop = () => {};

// The Home concepts on the concepting page, as they are, so this page points at
// them rather than drawing them again.
const concept = (initial: Parameters<typeof Flow>[0]['initial']) => () => (
  <Flow initial={initial} option={2} version='2.0' />
);

const CALCULATOR = `Check what you and ${NAME} could be entitled to`;
const CARE = `Find carers near ${NAME}`;
export const CALL = 'Book a call with a care expert';
export const CARE_ASSESSMENT = `Get ${NAME}'s care needs assessed`;
const FINANCIAL_ASSESSMENT = 'Get a financial assessment';

const COULD_GET = [
  'Attendance Allowance, £76.70 to £114.60 a week',
  'Council Tax Reduction',
  'Pension Credit',
];

// What onboarding heard, in the groups a profile would keep it in.
const TOLD_US = [
  { label: `About ${NAME}`, items: ['Lives in England', '66 or over'] },
  {
    label: 'Their care',
    items: ['Needs help day and night', 'Lives at home, with family nearby'],
  },
  { label: 'Money', items: ['Owns their home', 'No benefits yet'] },
];

/** The calculator on its own card: slice 4, scene 2 on the slices page. */
export const calculatorCard = (body?: string) => (
  <HomeConcept2
    todo
    conversations={false}
    todoList={{
      label: 'Things we can help with',
      body:
        body ??
        `From what you've told us, we could help you work out what you and ${NAME} are entitled to.`,
      items: [],
      action: 'Check eligibility',
    }}
  />
);

export const couldGet = (onItem?: (index: number) => void) => (
  <FromUsCard
    label={`What ${NAME} could get`}
    items={COULD_GET}
    sentence
    onItem={onItem}
  />
);

export const options = (label: string, items: string[], body?: string) => (
  <FromUsCard label={label} body={body} sentence>
    <Stack spacing={1}>
      {items.map((item) => (
        <ChatOption key={item} label={item} onClick={noop} />
      ))}
    </Stack>
  </FromUsCard>
);

const toldUs = (groups = TOLD_US) => (
  <Stack spacing={2}>
    {groups.map((group) => (
      <FromUsCard
        key={group.label}
        label={group.label}
        items={group.items}
        sentence
        onEdit={noop}
      />
    ))}
  </Stack>
);

// Actions, as the tab draws them: the bar under it and the person in the bar.
const actionsTab = (actions: Item[]) => (
  <AppTab active='actions'>
    <ActionsScreen title={NAME} initial={{ actions }} />
  </AppTab>
);

const action = (
  id: number,
  title: string,
  schedule: string,
  createdBy = 'You',
): Item => ({
  id,
  title,
  schedule,
  createdBy,
  kind: 'user',
  files: [],
  who: 'OP',
});

const PICKED = action(
  1,
  CALCULATOR,
  'You picked this at the end of onboarding',
);
const HALF_DONE = action(1, CALCULATOR, '3 of 8 questions answered');
const FROM_CHAT = [
  action(
    2,
    'Start the Attendance Allowance claim',
    'From your conversation about money',
  ),
  action(
    3,
    `Find ${NAME}'s National Insurance number`,
    'For the Attendance Allowance claim',
  ),
  action(
    4,
    `Ask the GP for a list of ${NAME}'s medication`,
    'For the Attendance Allowance claim',
  ),
];

// The calculator's conversation, answered up to its end.
const ANSWERED: ChatQuestion[] = [
  { ...WITH_HELD[0] },
  { ...WITH_HELD[1], answer: 'No' },
];

const RESULTS_END: ChatQuestion[] = [
  ...ANSWERED,
  {
    id: 'results',
    says: ["That's everything we need."],
    card: { label: `What ${NAME} could get`, items: COULD_GET, sentence: true },
    ask: 'We can help you claim these. Where would you like to start?',
    mode: 'say',
    attachRows: { more: ['Do this later'] },
    asides: [
      { label: 'Start the Attendance Allowance claim' },
      { label: CALL },
    ],
  },
];

const RESULTS_EACH: ChatQuestion[] = [
  ...ANSWERED,
  {
    id: 'results',
    says: ["That's everything we need."],
    ask: `Here's what ${NAME} could get. Pick one and we'll help you claim it.`,
    mode: 'say',
    // The calculator's own result rows, so the chat hands back what the app
    // shows for a result everywhere else.
    claims: CALCULATOR_RESULT.claims,
  },
];

const RESULTS_MID: ChatQuestion[] = [
  { ...WITH_HELD[0] },
  {
    id: 'early',
    says: [
      `Good news. From what you've said, ${NAME} could get Attendance Allowance.`,
    ],
    ask: `A few more questions to see what else. Is ${NAME} living in a care or nursing home?`,
    mode: 'say',
    quickReplies: [{ label: 'Yes' }, { label: 'No' }],
  },
];

const profileTab = () => (
  <AppTab active='profile'>
    <StatusBar />
    <AccountBar onSignOut={noop} />
    <Stack sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 2 }} spacing={2}>
      <Typography variant='h3'>{`What you've told us`}</Typography>
      {toldUs()}
    </Stack>
  </AppTab>
);

/**
 * What dynamic flows could mean for the rest of the app, one row per part of
 * it. Every title says the slice it becomes possible in, from the approach doc,
 * so the page answers what we could build now as well as what it could be.
 */
export const dynamicFlowAppSpec: HandoverSpec = {
  id: 'dynamic-flow-app',
  title: 'Dynamic flows, across the app',
  description: '',
  canvas: true,
  fixedHeadings: true,
  links: [
    {
      label: 'Dynamic flows: approach (Notion)',
      url: 'https://app.notion.com/p/karehero/Dynamic-flows-Benefits-calc-3fb63e8971dd8077b6b7e39eb33b02f7',
    },
    {
      label: 'Dynamic flows, in slices',
      url: '/tell-us-once/flows/dynamic-flow-slices',
    },
    {
      label: 'Dynamic flow concepting',
      url: '/tell-us-once/flows/dynamic-flow',
    },
  ],
  flows: [
    {
      heading: 'Home',
      title: 'Home: what it shows after onboarding',
      arrows: false,
      numbered: false,
      steps: [
        {
          id: 'home-today',
          title: 'Today: the same for everyone',
          render: () => (
            <AppTab active='home'>
              <HomeScreen />
            </AppTab>
          ),
        },
        {
          id: 'home-one',
          title: 'Slice 4 · One card at a time',
          render: () => homeTab(calculatorCard()),
          alt: {
            title: 'Slice 5 · Calculator done, its result in its place',
            render: () =>
              homeTab(
                <Stack spacing={3}>
                  {couldGet(noop)}
                  {options('Next', ['Start the Attendance Allowance claim'])}
                </Stack>,
              ),
          },
        },
        {
          id: 'home-ranked',
          title: 'Slice 4 · The calculator, then the rest',
          render: () =>
            homeTab(
              <Stack spacing={3}>
                {calculatorCard()}
                {options('Also for you', [
                  CARE_ASSESSMENT,
                  FINANCIAL_ASSESSMENT,
                  CALL,
                ])}
              </Stack>,
            ),
          alt: {
            title: 'Only what applies to them',
            render: () =>
              homeTab(
                <Stack spacing={3}>
                  {calculatorCard()}
                  {options('Also for you', [CALL])}
                </Stack>,
              ),
          },
        },
        {
          id: 'home-benefits',
          title: 'Slice 4 · What they could get, still empty',
          render: () =>
            homeTab(
              <Stack spacing={3}>
                {calculatorCard(
                  `Answer a few questions and we'll show what you and ${NAME} could get here.`,
                )}
              </Stack>,
            ),
          alt: {
            title: 'Slice 5 · Filled in by the calculator',
            render: () =>
              homeTab(
                <Stack spacing={3}>
                  {couldGet(noop)}
                  {options('Also for you', [CARE_ASSESSMENT, CALL])}
                </Stack>,
              ),
          },
        },
        {
          id: 'home-chat',
          title: 'Slice 7+ · Home as the chat (Concept 1)',
          render: concept('home-conversations'),
        },
      ],
    },
    {
      heading: 'Actions',
      title: 'Actions: made by a conversation, and a way back into one',
      arrows: false,
      numbered: false,
      steps: [
        {
          id: 'actions-picked',
          title: 'Slice 4 · Picked at the end of onboarding',
          render: () => actionsTab([PICKED]),
        },
        {
          id: 'actions-opens',
          title: 'Slice 4 · Tapped, it opens its conversation',
          render: () => chat(WITH_HELD),
        },
        {
          id: 'actions-half',
          title: 'Slice 5 · Half done, it says how far',
          render: () => actionsTab([HALF_DONE]),
          alt: {
            title: 'Back in, where they left off',
            render: () => chat(WITH_HELD, 1),
          },
        },
        {
          id: 'actions-from-chat',
          title: 'Slice 7 · Filled in by the conversation',
          render: () => actionsTab(FROM_CHAT),
        },
      ],
    },
    {
      heading: "What they've told us",
      title: "What they've told us: somewhere to see and edit it",
      arrows: false,
      numbered: false,
      steps: [
        {
          id: 'told-chat',
          title: 'Slice 3 · Only inside each conversation',
          render: () => chat(WITH_HELD),
        },
        {
          id: 'told-home',
          title: 'Slice 4 · On Home, under what to do next',
          render: () =>
            homeTab(
              <Stack spacing={3}>
                {calculatorCard()}
                <FromUsCard
                  label="What you've told us"
                  items={HELD}
                  sentence
                  onEdit={noop}
                />
              </Stack>,
            ),
        },
        {
          id: 'told-profile',
          title: 'Slice 4 · In Profile, grouped',
          render: profileTab,
        },
      ],
    },
    {
      heading: 'Results',
      title: 'Calculator results',
      arrows: false,
      numbered: false,
      steps: [
        {
          id: 'results-end',
          title: 'Slice 5 · At the end, then where to start',
          render: () => chat(RESULTS_END, 2),
        },
        {
          id: 'results-each',
          title: 'Slice 5 · Each one its own way in',
          render: () => chat(RESULTS_EACH, 2),
        },
        {
          id: 'results-mid',
          title: 'Slice 6 · As soon as we know, mid-chat',
          render: () => chat(RESULTS_MID, 1),
        },
        {
          id: 'results-home',
          title: 'Slice 5 · Kept on Home',
          render: () => homeTab(couldGet(noop)),
        },
      ],
    },
    {
      heading: 'Finding conversations',
      title: 'Finding conversations, and the UI map',
      arrows: false,
      numbered: false,
      steps: [
        {
          id: 'find-actions',
          title: 'Slice 4 · Only through actions',
          render: () => actionsTab([HALF_DONE, ...FROM_CHAT.slice(0, 1)]),
        },
        {
          id: 'find-home',
          title: 'Slice 6 · Listed on Home (Concept 2)',
          render: concept('home-concept-2'),
        },
        {
          id: 'find-menu',
          title: 'Slice 6 · In the menu (Concept 1.1)',
          render: concept('home-conversations-menu'),
          note: (
            <Note title='Note' variant='body2'>
              On a phone the menu is the side panel. A panel that stays open,
              like Claude&apos;s, only fits on desktop, and that is the big
              restructure.
            </Note>
          ),
        },
      ],
    },
    {
      heading: 'Generative UI',
      title: 'The same Home, by how much the app decides',
      arrows: false,
      numbered: false,
      note: (
        <Note title='Readme' variant='body2'>
          The first two are in the plan. The third needs the list of what we
          offer that routing needs. The fourth is not in the plan: the chat
          already picks its input from Loft&apos;s patterns, and this is the
          same idea on Home.
        </Note>
      ),
      steps: [
        {
          id: 'gen-fixed',
          title: 'Today · Fixed, the same for everyone',
          render: () => (
            <AppTab active='home'>
              <HomeScreen />
            </AppTab>
          ),
        },
        {
          id: 'gen-rules',
          title: 'Slice 4 · Rules on what they picked',
          render: () => homeTab(calculatorCard()),
        },
        {
          id: 'gen-ranked',
          title: 'Slices 6 to 7 · Ranked from a set of cards',
          render: () =>
            homeTab(
              <Stack spacing={3}>
                {options(
                  'Things we can help with',
                  [CARE, CALCULATOR, CALL],
                  "From what you've told us, finding care comes first.",
                )}
              </Stack>,
            ),
        },
        {
          id: 'gen-composed',
          title: 'Beyond the plan · Put together for them',
          render: () =>
            homeTab(
              <Stack spacing={3}>
                <FromUsCard
                  label="Before Thursday's call"
                  body={`Your care expert will ask about ${NAME}'s money. These would help.`}
                  items={[
                    `${NAME}'s National Insurance number`,
                    'A recent bank statement',
                  ]}
                  sentence
                  checked={-1}
                >
                  <Button
                    variant='primary'
                    isFullWidth
                    ariaLabel='Send them now'
                    onPress={noop}
                  >
                    Send them now
                  </Button>
                </FromUsCard>
                {couldGet(noop)}
              </Stack>,
            ),
        },
      ],
    },
  ],
  states: [],
  components: {},
};
